import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ConstructionIcon from "@mui/icons-material/Construction";
import MenuIcon from "@mui/icons-material/Menu";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import NotificationsIcon from "@mui/icons-material/Notifications";
import AppBar from "@mui/material/AppBar";
import IconButton from "@mui/material/IconButton";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import { styled } from "@mui/material/styles";
import { unwrapResult } from "@reduxjs/toolkit";
import ConnectionStatusIcon from "components/ConnectionStatusIcon";
import StyledButtonSecondaryGreyMini from "components/StyledButtonSecondaryGreyMini";
import { JobStatus } from "operations/schema/schema";
import { FC, useCallback, useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { shallowEqual } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "store";
import { selectConnectionStatus } from "store/root.store";
import {
  getNotifications,
  selectNotificationsCount,
  setNotificationsOpen,
} from "store/slices/comments.store";
import { setOpen as setDialogOpen } from "store/slices/dialog.store";
import { getTechnicianHistory } from "store/slices/history.store";
import {
  clearNewJobIds,
  downloadJobs,
  getJobs,
  selectIncompleteJobs,
  selectUnusedPreOrderedPartsCount,
  setViewUnusedPreOrderedPartsOpen,
} from "store/slices/jobs.store";
import { getPlannerJobs } from "store/slices/planner.store";
import { isFlagEnabled } from "store/slices/user.store";
import { SignoutDialog } from "../dialogs/SignoutDialog";
import { MenuDrawer } from "./MenuDrawer";
import { RightMenuDrawer } from "./RightMenuDrawer";

const PREFIX = "MenuAppBar";

const classes = {
  menuButton: `${PREFIX}-menuButton`,
  hidden: `${PREFIX}-hidden`,
};

const Root = styled("div")(({ theme }) => ({
  [`& .${classes.menuButton}`]: {
    marginRight: theme.spacing(2),
  },

  [`& .${classes.hidden}`]: {
    visibility: "hidden",
  },
}));

interface MenuAppBarProps {
  partnerView?: boolean;
}

export const MenuAppBar: FC<MenuAppBarProps> = ({ partnerView }) => {
  const intl = useIntl();
  const isOnline = useAppSelector(selectConnectionStatus);
  const dispatch = useAppDispatch();
  const { page } = useAppSelector((s) => s.root);
  const {
    appBar: { title, goingBack, sortingType, refetchEnabled, backAction, unusedPartsButton },
  } = useAppSelector((s) => s.menu);
  const [openMenu, setOpenMenu] = useState<boolean>(false);
  const [openSortingMenu, setOpenSortingMenu] = useState<boolean>(false);
  const navigate = useNavigate();
  const unusedPreOrderedPartsCount = useAppSelector(selectUnusedPreOrderedPartsCount);
  const showUnusedParts = page === "jobs" && unusedPartsButton && unusedPreOrderedPartsCount > 0;
  const notificationsCount = useAppSelector(selectNotificationsCount);
  const jobs = useAppSelector(selectIncompleteJobs, shallowEqual);
  const commentsEnabled = useAppSelector((s) => isFlagEnabled(s, "Comments"));

  useEffect(() => {
    if (commentsEnabled) dispatch(getNotifications());
  }, [commentsEnabled, dispatch]);

  const openOfflineQueue = useCallback(
    (open: boolean) => {
      dispatch(setDialogOpen({ dialogName: "queue", open }));
    },
    [dispatch],
  );

  const onRefetch = useCallback(() => {
    if (commentsEnabled) dispatch(getNotifications());
    switch (page) {
      case "jobs":
        dispatch(getJobs({ force: true }))
          .then(unwrapResult)
          .then(() => {
            if (jobs.length > 0) {
              var jobIds: string[] = [];
              jobs.forEach((job) => {
                if (job.status === JobStatus.Assigned) {
                  jobIds.push(job.id);
                }
              });
              if (jobIds.length > 0) {
                dispatch(downloadJobs({ jobIds }));
              }
            }
          });
        break;
      case "planner":
        dispatch(getPlannerJobs());
        break;
      case "history":
        dispatch(getTechnicianHistory());
        break;
    }
  }, [commentsEnabled, dispatch, jobs, page]);

  const onBackAction = useCallback(() => {
    if (backAction === "ClearNewJobIds") {
      dispatch(clearNewJobIds());
    }
  }, [backAction, dispatch]);

  const topLeftButton = goingBack ? (
    <IconButton
      edge="start"
      className={classes.menuButton}
      color="inherit"
      aria-label={intl.formatMessage({ id: "general.back" })}
      onClick={() => {
        if (window.history.state && window.history.state.idx > 0) {
          navigate(-1);
        } else {
          navigate("/", { replace: true });
        }
        onBackAction();
      }}
      size="large"
    >
      <ArrowBackIcon />
    </IconButton>
  ) : (
    <IconButton
      edge="start"
      className={`${classes.menuButton}`}
      color="inherit"
      aria-label={intl.formatMessage({ id: "menu" })}
      onClick={() => setOpenMenu(true)}
      size="large"
    >
      <MenuIcon />
    </IconButton>
  );

  return (
    <Root sx={{ flexGrow: 1 }}>
      <SignoutDialog partnerView={partnerView} />
      <AppBar
        position="fixed"
        data-testid="MenuAppBar"
        sx={{
          backgroundColor: "primary.dark",
        }}
      >
        <Toolbar>
          {topLeftButton}
          {/*fake dom element to keep title centered*/}
          {!isOnline && (
            <div
              style={{
                visibility: "hidden",
                minWidth: 64,
              }}
            />
          )}
          <Typography
            onClick={() => (refetchEnabled ? onRefetch() : {})}
            textAlign="center"
            className="truncated"
            ml="auto"
            mr="auto"
            data-testid="menuTitle"
          >
            {title}
          </Typography>
          {!isOnline && <ConnectionStatusIcon onClick={() => openOfflineQueue(true)} />}
          {showUnusedParts && (
            <StyledButtonSecondaryGreyMini
              onClick={() => dispatch(setViewUnusedPreOrderedPartsOpen({ open: true }))}
              endIcon={<ConstructionIcon />}
            >
              {unusedPreOrderedPartsCount}
            </StyledButtonSecondaryGreyMini>
          )}
          {commentsEnabled && (
            <StyledButtonSecondaryGreyMini
              onClick={() => dispatch(setNotificationsOpen({ open: true }))}
              endIcon={<NotificationsIcon />}
            >
              {notificationsCount}
            </StyledButtonSecondaryGreyMini>
          )}
          {!!sortingType && (
            <IconButton
              edge="end"
              color="inherit"
              aria-label={intl.formatMessage({ id: "menu" })}
              onClick={() => setOpenSortingMenu(true)}
              data-testid="MenuAppBar-SortingMenu-ButtonOpen"
              size="large"
            >
              <MoreVertIcon />
            </IconButton>
          )}
        </Toolbar>
      </AppBar>
      <Toolbar />

      <MenuDrawer openMenu={openMenu} setOpenMenu={setOpenMenu} partnerView={partnerView} />

      <RightMenuDrawer openMenu={openSortingMenu} setOpenMenu={setOpenSortingMenu} />
    </Root>
  );
};
